/**
 * Heatmap — stores (rows) x day-parts (cols) grid with green→amber→red tint.
 * `invert` = true when a HIGHER value is worse (speed of service, labour %).
 * Cells stagger-fade on mount.
 */
import React from 'react';
import AskCorvana from './AskCorvana';

function colorFor(t) {
  // t in [0,1], 0 = good (green), 1 = bad (red)
  const stops = [
    [16, 185, 129], // emerald
    [245, 158, 11], // amber
    [239, 68, 68], // red
  ];
  const seg = t < 0.5 ? 0 : 1;
  const local = t < 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
  const a = stops[seg], b = stops[seg + 1];
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * local));
  return `rgba(${c[0]},${c[1]},${c[2]},0.16)`;
}

export default function Heatmap({ rows, cols, values, unit = '', invert = false, title, ai }) {
  const flat = values.flat();
  const min = Math.min(...flat), max = Math.max(...flat), span = max - min || 1;
  return (
    <div className="rounded-2xl qsr-card p-4 shadow-sm" data-testid="qsr-heatmap">
      {(title || ai) && (
        <div className="flex items-center justify-between mb-3">
          {title && <div className="text-sm font-semibold text-slate-900">{title}</div>}
          {ai && <AskCorvana insight={ai} />}
        </div>
      )}
      <div className="overflow-x-auto">
        <div className="min-w-[520px]">
          <div className="grid" style={{ gridTemplateColumns: `110px repeat(${cols.length}, 1fr)` }}>
            <div />
            {cols.map((c) => (
              <div key={c} className="text-[10px] text-slate-500 text-center pb-1.5 font-medium">{c}</div>
            ))}
            {rows.map((r, ri) => (
              <React.Fragment key={r}>
                <div className="text-xs font-medium text-slate-700 flex items-center pr-2">{r}</div>
                {values[ri].map((v, ci) => {
                  let t = (v - min) / span;
                  if (!invert) t = 1 - t;
                  return (
                    <div
                      key={ci}
                      className="m-0.5 rounded-lg grid place-items-center text-[11px] font-semibold text-slate-800 h-9 animate-in fade-in zoom-in-95"
                      style={{ backgroundColor: colorFor(t), animationDelay: `${(ri * cols.length + ci) * 22}ms`, animationDuration: '500ms', animationFillMode: 'both' }}
                    >
                      {v}{unit}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
